import { useState, useCallback, useRef } from "react";
import { fetchCatalogMock } from "../../mocks/catalogMock";
import type { CatalogParentItem, CatalogFilterType } from "../../types/catalog";
import {
  CATALOG_FILTER_TYPE_PARAM,
  CATALOG_LIST_URL,
  CATALOG_LOAD_ERROR_MESSAGE,
  CATALOG_SEARCH_PARAM,
  mapCatalogListResponse,
  resolveCatalogLoadError,
  translateCatalogMessage,
} from "../../types/catalog";
import {
  getSessionRequestCache,
  setSessionRequestCache,
} from "../../utils/sessionRequestCache";

interface FetchCatalogOptions {
  force?: boolean;
}

interface UseGetCatalogReturn {
  catalog: CatalogParentItem[] | null;
  loading: boolean;
  error: string | null;
  totalElements: number;
  fetchCatalog: (
    page: number,
    size: number,
    search?: string,
    filterType?: CatalogFilterType,
    options?: FetchCatalogOptions
  ) => Promise<void>;
}

const shouldUseMockCatalog = (): boolean =>
  process.env.REACT_APP_USE_CATALOG_MOCK === "true";

const authHeaders = (token: string): HeadersInit => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
});

const useGetCatalog = (): UseGetCatalogReturn => {
  const [catalog, setCatalog] = useState<CatalogParentItem[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchCatalog = useCallback(
    async (
      page: number,
      size: number,
      search = "",
      filterType?: CatalogFilterType,
      options?: FetchCatalogOptions
    ): Promise<void> => {
      const normalizedSearch = search.trim();
      const cacheKey = `catalog:list:${page}:${size}:${normalizedSearch}:${filterType ?? ""}`;
      const requestId = ++requestIdRef.current;

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const isLatestRequest = (): boolean =>
        requestId === requestIdRef.current;

      if (!options?.force && !shouldUseMockCatalog()) {
        const cached = getSessionRequestCache<{
          content: CatalogParentItem[];
          totalElements: number;
        }>(cacheKey);
        if (cached) {
          if (!isLatestRequest()) return;
          setCatalog(cached.content);
          setTotalElements(cached.totalElements);
          setError(null);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);

      try {
        if (shouldUseMockCatalog()) {
          const data = fetchCatalogMock(
            page,
            size,
            normalizedSearch,
            filterType
          );
          if (!isLatestRequest()) return;
          setCatalog(data.content);
          setTotalElements(data.totalElements);
          return;
        }

        const tokenStorage = localStorage.getItem("token");
        const token = tokenStorage ? tokenStorage.replace(/['"]+/g, "") : null;

        if (!token) {
          throw new Error("Token de acceso no encontrado. Inicia sesión primero.");
        }

        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        if (normalizedSearch) {
          params.set(CATALOG_SEARCH_PARAM, normalizedSearch);
          if (filterType) {
            params.set(CATALOG_FILTER_TYPE_PARAM, filterType);
          }
        }

        const response = await fetch(
          `${CATALOG_LIST_URL}?${params.toString()}`,
          {
            method: "GET",
            headers: authHeaders(token),
            signal: abortController.signal,
          }
        );

        if (!isLatestRequest()) return;

        if (!response.ok) {
          throw new Error(await resolveCatalogLoadError(response));
        }

        const raw = await response.json();
        const data = mapCatalogListResponse(raw);

        if (!isLatestRequest()) return;

        setSessionRequestCache(cacheKey, {
          content: data.content,
          totalElements: data.totalElements,
        });
        setCatalog(data.content);
        setTotalElements(data.totalElements);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        if (!isLatestRequest()) return;
        setCatalog([]);
        setTotalElements(0);
        const message =
          err instanceof Error
            ? translateCatalogMessage(err.message, CATALOG_LOAD_ERROR_MESSAGE)
            : CATALOG_LOAD_ERROR_MESSAGE;
        setError(message);
      } finally {
        if (isLatestRequest()) {
          setLoading(false);
        }
      }
    },
    []
  );

  return { catalog, loading, error, totalElements, fetchCatalog };
};

export default useGetCatalog;
